import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Plus, Settings, Trash2 } from "lucide-react";
import BoardColumn from "../components/BoardColumn";
import CardModal from "../components/CardModal";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function BoardView() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [lists, setLists] = useState([]);
  const [cards, setCards] = useState([]);
  const [newListTitle, setNewListTitle] = useState("");
  const [showNewList, setShowNewList] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [boardMembers, setBoardMembers] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchBoardData();
  }, [boardId]);

  useEffect(() => {
    // Check if there's a card parameter in URL and open it
    const urlParams = new URLSearchParams(window.location.search);
    const cardId = urlParams.get('card');
    
    if (cardId && cards.length > 0) {
      const card = cards.find(c => c.id === cardId);
      if (card) {
        setSelectedCard(card);
        // Remove the query parameter from URL
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [cards]);

  const fetchBoardData = async () => {
    try {
      const [boardRes, listsRes, cardsRes, membersRes] = await Promise.all([
        axios.get(`/boards`),
        axios.get(`/boards/${boardId}/lists`),
        axios.get(`/boards/${boardId}/cards`),
        axios.get(`/boards/${boardId}/members`),
      ]);
      const currentBoard = boardRes.data.find((b) => b.id === boardId);
      setBoard(currentBoard);
      setLists(listsRes.data);
      setCards(cardsRes.data);
      setBoardMembers(membersRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden des Boards");
    } finally {
      setLoading(false);
    }
  };

  const createList = async () => {
    if (!newListTitle.trim()) {
      toast.error("Bitte geben Sie einen Titel ein");
      return;
    }

    try {
      const response = await axios.post(`/boards/${boardId}/lists`, {
        title: newListTitle,
      });
      setLists([...lists, response.data]);
      setNewListTitle("");
      setShowNewList(false);
      toast.success("Liste erstellt!");
    } catch (error) {
      toast.error("Fehler beim Erstellen der Liste");
    }
  };

  const deleteList = async (listId) => {
    try {
      await axios.delete(`/lists/${listId}`);
      setLists(lists.filter((l) => l.id !== listId));
      setCards(cards.filter((c) => c.listId !== listId));
      toast.success("Liste gelöscht!");
    } catch (error) {
      toast.error("Fehler beim Löschen der Liste");
    }
  };

  const deleteBoard = async () => {
    try {
      await axios.delete(`/boards/${boardId}`);
      toast.success("Board gelöscht!");
      navigate("/");
    } catch (error) {
      toast.error("Fehler beim Löschen des Boards");
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const card = cards.find((c) => c.id === active.id);
    setActiveCard(card);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveCard(null);

    if (!over) return;

    const activeCard = cards.find((c) => c.id === active.id);
    if (!activeCard) return;

    // Determine target list
    let targetListId = activeCard.listId;
    
    if (over.id.startsWith("list-")) {
      targetListId = over.id.replace("list-", "");
    } else {
      const overCard = cards.find((c) => c.id === over.id);
      if (overCard) {
        targetListId = overCard.listId;
      }
    }

    // Only update if list changed
    if (activeCard.listId !== targetListId) {
      try {
        await axios.put(`/cards/${activeCard.id}`, {
          listId: targetListId,
        });

        // Update local state immediately for better UX
        setCards(
          cards.map((card) =>
            card.id === activeCard.id ? { ...card, listId: targetListId } : card
          )
        );
        
        toast.success("Karte verschoben!");
      } catch (error) {
        toast.error("Fehler beim Verschieben der Karte");
        // Revert on error
        fetchBoardData();
      }
    }
  };

  const addCard = async (listId, title) => {
    try {
      const response = await axios.post(`/lists/${listId}/cards`, {
        title,
        description: "",
        labels: [],
        dueDate: null,
      });
      setCards([...cards, response.data]);
      toast.success("Karte erstellt!");
    } catch (error) {
      toast.error("Fehler beim Erstellen der Karte");
    }
  };

  const updateCard = async (cardId, updates) => {
    try {
      const response = await axios.put(`/cards/${cardId}`, updates);
      setCards(cards.map((card) => (card.id === cardId ? response.data : card)));
      toast.success("Karte aktualisiert!");
    } catch (error) {
      toast.error("Fehler beim Aktualisieren der Karte");
    }
  };

  const deleteCard = async (cardId) => {
    try {
      await axios.delete(`/cards/${cardId}`);
      setCards(cards.filter((c) => c.id !== cardId));
      toast.success("Karte gelöscht!");
    } catch (error) {
      toast.error("Fehler beim Löschen der Karte");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Board nicht gefunden</h2>
          <Button onClick={() => navigate("/")}>Zurück zum Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                data-testid="back-button"
                variant="outline"
                size="icon"
                onClick={() => navigate("/")}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="board-title">{board.name}</h1>
            </div>
            
            {/* Board Members */}
            {boardMembers.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 font-medium">Mitglieder:</span>
                <div className="flex -space-x-2">
                  {boardMembers.slice(0, 5).map((member, idx) => (
                    <div
                      key={member.id}
                      className="relative"
                      title={member.name || member.username}
                    >
                      {member.avatarUrl ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${member.avatarUrl}`}
                          alt={member.name || member.username}
                          className="w-10 h-10 rounded-full border-2 border-white object-cover shadow-sm hover:scale-110 transition-transform"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold shadow-sm hover:scale-110 transition-transform">
                          {(member.name || member.username).charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                  ))}
                  {boardMembers.length > 5 && (
                    <div 
                      className="w-10 h-10 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center shadow-sm"
                      title={`${boardMembers.length - 5} weitere Mitglieder`}
                    >
                      <span className="text-xs font-medium text-gray-600">
                        +{boardMembers.length - 5}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="delete-board-button" variant="destructive" size="sm" className="gap-2">
                  <Trash2 className="w-4 h-4" />
                  Board löschen
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Board wirklich löschen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Diese Aktion kann nicht rückgängig gemacht werden. Alle Listen und Karten werden ebenfalls gelöscht.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteBoard} className="bg-red-600 hover:bg-red-700">
                    Löschen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </header>

      {/* Board Content */}
      <main className="p-6 overflow-x-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-6 pb-6">
            {lists.map((list) => (
              <BoardColumn
                key={list.id}
                list={list}
                cards={cards.filter((card) => card.listId === list.id)}
                onAddCard={addCard}
                onDeleteList={deleteList}
                onCardClick={setSelectedCard}
              />
            ))}

            {/* Add New List */}
            {showNewList ? (
              <div className="min-w-[320px] bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm border border-gray-200">
                <Input
                  data-testid="new-list-input"
                  placeholder="Listentitel eingeben..."
                  value={newListTitle}
                  onChange={(e) => setNewListTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createList()}
                  autoFocus
                  className="mb-2"
                />
                <div className="flex gap-2">
                  <Button data-testid="create-list-button" onClick={createList} size="sm" className="flex-1">
                    Liste hinzufügen
                  </Button>
                  <Button
                    data-testid="cancel-list-button"
                    onClick={() => {
                      setShowNewList(false);
                      setNewListTitle("");
                    }}
                    variant="outline"
                    size="sm"
                  >
                    Abbrechen
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                data-testid="add-list-button"
                onClick={() => setShowNewList(true)}
                variant="outline"
                className="min-w-[320px] h-auto py-8 border-2 border-dashed hover:bg-white/50 backdrop-blur-sm"
              >
                <Plus className="w-5 h-5 mr-2" />
                Liste hinzufügen
              </Button>
            )}
          </div>
        </DndContext>
      </main>

      {/* Card Modal */}
      {selectedCard && (
        <CardModal
          card={selectedCard}
          boardId={boardId}
          onClose={() => setSelectedCard(null)}
          onUpdate={updateCard}
          onDelete={deleteCard}
        />
      )}
    </div>
  );
}
